/* Copyright (c) 2020-present CloudIO Inc */

import { Box, Direction, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import {
  useIsRecordAtIndexCurrent,
  useIsRecordAtIndexDeleted,
  useSetCurrentRecord,
} from 'components/DataStoreReference/DataStoreReference';
// import useWhyDidYouUpdate from 'hooks/useWhyDidYouUpdate';
import React, { memo, useMemo } from 'react';
import useTrigger from 'triggers/useTrigger';
import { AnyColumn, ItemID } from '../../../componentregistry';
import { usePending } from '../../../platform/PlatformHooks';
import Store from '../../../store/Store';
import { useTheme } from '../../../theme/Theme';
import useRow from './useRow';
import CellWrapper from './CellWrapper';
import { hoverRowState } from './state';
import {
  Cell,
  CellComponentProps,
  DataContext,
  isBodyRegion,
  Region,
} from './types';
import { getController } from 'controller/ControllerRegistry';
import { DataRecord } from '@cloudio-saas/datasource-types';

const useStyles = makeStyles((theme: Theme) => ({
  fixed: {
    zIndex: 2,
  },
  row: {
    position: 'absolute',
    // width: '100%',
  },
  highlightRow: {
    backgroundColor: theme.palette.action.hover,
  },
  even: {},
  odd: {
    backgroundColor: theme.palette.action.hover,
  },
  activeRow: {
    backgroundColor: theme.palette.action.selected,
  },
  clickable: {
    cursor: 'pointer',
  },
  deleted: {
    textDecoration: 'line-through',
    '& .io-cell': {
      textDecoration: 'line-through',
    },
  },
}));

function Row(props: {
  CellComponent: React.FunctionComponent<CellComponentProps>;
  appUid: string;
  columnLefts?: number[];
  columnWidths: number[];
  columns: AnyColumn[];
  custom: boolean;
  dataContext: DataContext;
  direction?: Direction;
  endColumn: number;
  fixedLeftColumns: number;
  headerRows: number;
  highlightCell?: boolean;
  highlightRow?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>, cell: Cell<AnyColumn>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onRowDoubleClick?: (_rowIndex: number) => void;
  pageId: ItemID;
  region: Region;
  resetScroll: () => void;
  roleUid: string;
  rowHeight: number;
  rowIndex: number;
  showColumnLines: boolean;
  showRowLines: boolean;
  startColumn: number;
  top: number;
  totalRows: number;
  store: Store<DataRecord> | null;
  updateSelectedCells?: (
    rowIndex: number,
    columnIndex: number,
    value: string | number | undefined | null,
  ) => void;
}) {
  const {
    CellComponent,
    appUid,
    columnLefts,
    columnWidths,
    columns,
    custom,
    dataContext,
    direction,
    endColumn,
    fixedLeftColumns,
    headerRows,
    highlightCell,
    highlightRow,
    onClick,
    onKeyDown,
    onRowDoubleClick,
    pageId,
    region,
    resetScroll,
    roleUid,
    rowHeight,
    rowIndex,
    showColumnLines,
    showRowLines,
    startColumn,
    top,
    totalRows,
    store,
    updateSelectedCells,
  } = props;
  const theme = useTheme();
  const classes = useStyles();
  const bodyRegion = isBodyRegion(region);
  const activeRecord =
    useIsRecordAtIndexCurrent(
      bodyRegion ? rowIndex - headerRows : Number.MAX_VALUE,
    ) && !custom;

  const even = rowIndex % 2 === 0;

  const [onRowClickTrigger] = useTrigger(
    {
      platform: dataContext.platform,
      appUid,
      roleUid,
      pageId: dataContext.pageId,
      itemId: dataContext.itemId,
      disableProgressTracking: true,
    },
    'TriggerOnRowClick',
  );
  const [onRowDoubleClickTrigger] = useTrigger(
    {
      platform: dataContext.platform,
      appUid,
      roleUid,
      pageId: dataContext.pageId,
      itemId: dataContext.itemId,
      disableProgressTracking: true,
    },
    'TriggerOnRowDoubleClick',
  );

  const deletedRecord = useIsRecordAtIndexDeleted(
    bodyRegion ? rowIndex - headerRows : Number.MAX_VALUE,
  );

  const cn = clsx({
    [classes.row]: true,
    // [classes.highlightRow]:
    //   hoverRowIndex === rowIndex && highlightRow && bodyRegion,
    [classes.activeRow]: activeRecord && dataContext.highlightActiveRow,
    [classes.even]: dataContext.stripeRows && even,
    [classes.odd]: dataContext.stripeRows && !even,
    [classes.clickable]: onRowClickTrigger && bodyRegion,
    [classes.deleted]: deletedRecord,
  });
  const controller = getController(appUid, pageId);
  const colData = useRow({
    columns,
    rowIndex,
    appUid,
    gridId: dataContext.itemId,
    pageId,
    platform: dataContext.platform,
    region,
    store,
    classes: dataContext.cellClasses,
    userSetup: dataContext.userSetup,
    theme,
    controller,
  });

  const cols = useMemo(() => {
    const _cols: React.ReactNode[] = [];
    let left = 0;
    for (
      let columnIndex = startColumn;
      columnIndex <= endColumn && columnIndex < columns.length;
      columnIndex++
    ) {
      if (
        columnLefts &&
        ['body-center', 'header-center', 'footer-center'].includes(region)
      ) {
        left =
          columnLefts[columnIndex] -
          (fixedLeftColumns ? columnLefts[fixedLeftColumns] : 0);
      } else if (
        columnLefts &&
        ['body-left', 'header-left', 'footer-left'].includes(region)
      ) {
        left = columnLefts[columnIndex];
      }

      const className = [
        'body-center',
        'header-center',
        'footer-center',
      ].includes(region)
        ? null
        : classes.fixed;

      _cols.push(
        <CellWrapper
          CellComponent={CellComponent}
          appUid={appUid}
          className={className}
          columnIndex={columnIndex}
          columns={columns}
          custom={custom}
          dataContext={dataContext}
          direction={direction}
          highlightCell={highlightCell}
          highlightRow={highlightRow}
          key={`${region}-${columnIndex}`}
          left={left}
          onClick={onClick}
          onKeyDown={onKeyDown}
          pageId={pageId}
          region={region}
          resetScroll={resetScroll}
          roleUid={roleUid}
          rowHeight={rowHeight}
          rowIndex={rowIndex}
          showColumnLines={showColumnLines}
          showRowLines={showRowLines}
          top={top}
          totalRows={totalRows}
          width={columnWidths[columnIndex]}
          columnData={colData[columnIndex]}
          updateSelectedCells={updateSelectedCells}
          colData={colData}
        />,
      );

      if (['body-right', 'header-right', 'footer-right'].includes(region)) {
        left += columnWidths[columnIndex];
      }
    }
    return _cols;
  }, [
    CellComponent,
    appUid,
    classes.fixed,
    colData,
    columnLefts,
    columnWidths,
    columns,
    custom,
    dataContext,
    direction,
    endColumn,
    fixedLeftColumns,
    highlightCell,
    highlightRow,
    onClick,
    onKeyDown,
    pageId,
    region,
    resetScroll,
    roleUid,
    rowHeight,
    rowIndex,
    showColumnLines,
    showRowLines,
    startColumn,
    top,
    totalRows,
    updateSelectedCells,
  ]);

  const setCurrentRecord = useSetCurrentRecord();
  const [, setPending] = usePending();

  const _props: {
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    onDoubleClick?: (e: React.MouseEvent<HTMLElement>) => void;
  } = {};

  if (bodyRegion && onRowClickTrigger) {
    _props.onClick = async (e: React.MouseEvent<HTMLElement>) => {
      dataContext.platform.startAsyncTransition(
        async () => {
          setCurrentRecord(rowIndex - headerRows);
          await onRowClickTrigger(e);
        },
        setPending,
        { itemId: dataContext.itemId, pageId, appUid },
      );
    };
  }
  if (bodyRegion && (onRowDoubleClick || onRowDoubleClickTrigger)) {
    _props.onDoubleClick = async (e: React.MouseEvent<HTMLElement>) => {
      dataContext.platform.startAsyncTransition(
        async () => {
          setCurrentRecord(rowIndex - headerRows);
          if (onRowDoubleClick) {
            onRowDoubleClick(rowIndex - headerRows);
          }
          if (onRowDoubleClickTrigger) {
            await onRowDoubleClickTrigger(e);
          }
        },
        setPending,
        { itemId: dataContext.itemId, pageId, appUid },
      );
    };
  }

  // console.log('Row.render', rowIndex);
  // useWhyDidYouUpdate('Row', {
  //   CellComponent,
  //   appUid,
  //   colData,
  //   columnLefts,
  //   columnWidths,
  //   columns,
  //   custom,
  //   dataContext,
  //   direction,
  //   endColumn,
  //   fixedLeftColumns,
  //   highlightCell,
  //   highlightRow,
  //   onClick,
  //   onKeyDown,
  //   pageId,
  //   region,
  //   resetScroll,
  //   roleUid,
  //   rowHeight,
  //   rowIndex,
  //   showColumnLines,
  //   showRowLines,
  //   startColumn,
  //   top,
  // });

  const width = columnWidths.reduce((w, v) => w + v, 0);
  const expandLastColumn = dataContext.expandLastColumn;

  return (
    <Box
      className={cn}
      onMouseEnter={() =>
        hoverRowState.hoverRow.set({
          appUid,
          pageId: dataContext.pageId,
          itemId: dataContext.itemId,
          rowIndex,
        })
      }
      onMouseLeave={() =>
        hoverRowState.hoverRow.set({
          appUid,
          pageId: dataContext.pageId,
          itemId: dataContext.itemId,
          rowIndex: -1,
        })
      }
      sx={{
        top,
        height: rowHeight,
        '&:hover': highlightRow
          ? { backgroundColor: theme.palette.action.hover }
          : undefined,
        width: expandLastColumn ? '100%' : width,
      }}
      aria-rowindex={rowIndex}
      {..._props}
    >
      {cols}
    </Box>
  );
}

export default memo(Row);
